import { Placement } from '@popperjs/core';
import { isNil } from 'ramda';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';

import { ColorTypes } from '@folx-types/theme';

import TooltipArrow from '@assets/vectors/tooltip-arrow.inline.svg';
import TooltipIcon from '@assets/vectors/tooltip.inline.svg';

import theme from '@theme/FolxTheme';

export interface ITooltipProps {
	ariaLabel?: string;
	title?: string;
	autoWidth?: boolean;
	children?: Exclude<ReactNode, string | number | boolean>;
	className?: string;
	defaultIcon?: boolean;
	description?: string;
	fillColor?: ColorTypes;
	placement?: Placement;
	showArrow?: boolean;
}

export interface ITooltipComponent {
	active: boolean;
	hovering: boolean;
}

const TooltipPopper = styled.div<{ active: boolean; placement: Placement; autoWidth: boolean }>`
	background: ${theme.colors.black};
	border-radius: ${theme.radius.md};
	color: ${theme.colors.white};
	display: flex;
	flex-flow: column wrap;
	gap: ${theme.spacing.xxs};
	justify-content: flex-start;
	padding: ${theme.spacing.xssm};
	text-align: start;
	visibility: ${({ active }) => (active ? 'visible' : 'hidden')};
	width: ${({ autoWidth }) => (autoWidth ? 'auto' : '25rem')};
	z-index: ${theme.zIndexes.foreground};

	&[data-popper-placement^='top'] {
		margin: 0 0 ${theme.spacing.xxs} 0;
		svg {
			bottom: -0.9rem;
		}
	}

	&[data-popper-placement^='bottom'] {
		margin: ${theme.spacing.xxs} 0 0 0;
		svg {
			top: -0.9rem;
			transform: rotate(180deg);
		}
	}

	&[data-popper-placement^='left'] {
		margin: 0 ${theme.spacing.xxs} 0 0;
		svg {
			right: -1.3rem;
			transform: rotate(-90deg);
			top: calc(50% - 0.45rem);
		}
	}

	&[data-popper-placement^='right'] {
		margin: 0 0 0 ${theme.spacing.xxs};
		svg {
			left: -1.3rem;
			transform: rotate(90deg);
			top: calc(50% - 0.45rem);
		}
	}

	&[data-popper-placement*='-start'] > svg {
		left: 0.9rem;
	}

	&[data-popper-placement*='-end'] > svg {
		right: 0.9rem;
	}

	&[data-popper-placement='top'] > svg {
		left: calc(50% - 0.9rem);
	}

	&[data-popper-placement='bottom'] > svg {
		right: calc(50% - 0.9rem);
	}

	svg {
		width: 1.8rem;
		height: 0.9rem;
		position: absolute;
		path: {
			fill: ${theme.colors.black};
		}
	}
`;

const TooltipButton = styled.button<{
	active: boolean;
	showTooltip: boolean;
	fillColor?: ColorTypes;
}>`
	position: relative;
	padding: ${theme.spacing.xs};
	line-height: 0;

	:hover {
		svg {
			fill: ${({ showTooltip, fillColor }) => showTooltip && fillColor && theme.colors[fillColor]};
		}

		${TooltipPopper} {
			visibility: ${({ showTooltip }) => showTooltip && 'visible'};
		}
	}

	svg {
		fill: ${({ active, fillColor }) => active && fillColor && theme.colors[fillColor]};
		flex-shrink: 0;
	}
`;

const Tooltip: FC<ITooltipProps> = ({
	ariaLabel = 'More Info',
	autoWidth = false,
	children,
	className = '',
	description,
	fillColor,
	placement = 'auto',
	showArrow = false,
	title = '',
	defaultIcon = false,
}) => {
	const [active, setActive] = useState(false);
	const [showTooltip, setShowTooltip] = useState(false);

	const [tooltipRef, setTooltipRef] = useState(null);
	const [popperElement, setPopperElement] = useState(null);
	const [arrowElement, setArrowElement] = useState(null);

	const { styles, attributes, state, update } = usePopper(tooltipRef, popperElement, {
		placement: placement,
		modifiers: [
			{
				name: 'arrow',
				options: {
					element: arrowElement,
				},
			},
			{
				name: 'flip',
				options: {
					fallbackPlacements: [
						'top',
						'top-start',
						'top-end',
						'bottom',
						'bottom-start',
						'bottom-end',
						'left',
						'right',
					],
				},
			},
		],
	});

	useEffect(() => {
		if (isNil(update) || !active) {
			return;
		}
		update();
	}, [active]);

	return (
		<TooltipButton
			active={active}
			aria-label={ariaLabel}
			className={className}
			fillColor={fillColor}
			ref={setTooltipRef}
			showTooltip={showTooltip}
			type="button"
			onClick={(e) => {
				if (active) {
					setShowTooltip(false);
				}
				setActive(!active);
				e.stopPropagation();
			}}
			onMouseEnter={() => {
				setShowTooltip(true);
				!isNil(update) && update();
			}}
			onBlur={() => {
				setShowTooltip(false);
				setActive(false);
			}}>
			{defaultIcon ? <TooltipIcon /> : children}
			<TooltipPopper
				active={active}
				autoWidth={autoWidth}
				ref={setPopperElement}
				style={styles.popper}
				{...state}
				{...attributes.popper}>
				<BasicText variant="fineprintBold" color="white">
					{title}
				</BasicText>
				{description && (
					<BasicText variant="fineprint" color="white">
						{description}
					</BasicText>
				)}
				{showArrow && <TooltipArrow ref={setArrowElement} style={{ ...styles.arrow }} />}
			</TooltipPopper>
		</TooltipButton>
	);
};

export default styled(Tooltip)``;
